import type { CCCard } from "./types";

export type SourceFieldDef = {
  field: string;
  group: string;
  label: string;
  defaultHeader: string;
};

export const CARD_LINK_BASE = "https://collectorcrypt.com/assets/solana";

// Sentinel field name for "static value" preset columns. The value
// itself lives on the PresetColumn (column.value), not on the card row.
export const STATIC_FIELD = "static";

// All fields that the preset editor can pick from.
// Field keys mirror the Collector Crypt API names (so a path like
// "owner.wallet" resolves directly against the card object) plus a
// couple of computed virtual fields ("cardLink", "comp").
export const SOURCE_FIELDS: ReadonlyArray<SourceFieldDef> = [
  // Computed
  { field: "cardLink", group: "Computed", label: "Card link (URL)", defaultHeader: "Card Link" },
  { field: "comp", group: "Computed", label: "Comp (always empty)", defaultHeader: "Comp" },
  { field: STATIC_FIELD, group: "Computed", label: "Static value (custom)", defaultHeader: "Custom" },

  // Identity
  { field: "id", group: "Identity", label: "Card ID (CC internal)", defaultHeader: "Card ID" },
  { field: "nftAddress", group: "Identity", label: "Token / mint address", defaultHeader: "TokenID" },
  { field: "blockchain", group: "Identity", label: "Blockchain", defaultHeader: "Blockchain" },
  { field: "nftStandard", group: "Identity", label: "NFT standard", defaultHeader: "NFT Standard" },
  { field: "nftCategory", group: "Identity", label: "NFT category", defaultHeader: "NFT Category" },

  // Card details
  { field: "itemName", group: "Card details", label: "Item name", defaultHeader: "Name" },
  { field: "category", group: "Card details", label: "Category", defaultHeader: "Category" },
  { field: "type", group: "Card details", label: "Type", defaultHeader: "Type" },
  { field: "orientation", group: "Card details", label: "Orientation", defaultHeader: "Orientation" },
  { field: "year", group: "Card details", label: "Year", defaultHeader: "Year" },
  { field: "set", group: "Card details", label: "Set", defaultHeader: "Set" },
  { field: "serial", group: "Card details", label: "Serial", defaultHeader: "Serial" },
  { field: "subset", group: "Card details", label: "Subset", defaultHeader: "Subset" },
  { field: "parallel", group: "Card details", label: "Parallel", defaultHeader: "Parallel" },
  { field: "language", group: "Card details", label: "Language", defaultHeader: "Language" },
  { field: "format", group: "Card details", label: "Format", defaultHeader: "Format" },
  { field: "productCode", group: "Card details", label: "Product code", defaultHeader: "Product Code" },
  { field: "packCount", group: "Card details", label: "Pack count", defaultHeader: "Pack Count" },
  { field: "publisher", group: "Card details", label: "Publisher", defaultHeader: "Publisher" },
  { field: "platform", group: "Card details", label: "Platform", defaultHeader: "Platform" },
  { field: "rating", group: "Card details", label: "Rating", defaultHeader: "Rating" },
  { field: "series", group: "Card details", label: "Series", defaultHeader: "Series" },
  { field: "features", group: "Card details", label: "Features", defaultHeader: "Features" },
  { field: "authenticated", group: "Card details", label: "Authenticated", defaultHeader: "Authenticated" },
  { field: "autographed", group: "Card details", label: "Autographed", defaultHeader: "Autographed" },

  // Grading
  { field: "grade", group: "Grading", label: "Grade (text)", defaultHeader: "Grade" },
  { field: "gradeNum", group: "Grading", label: "Grade (numeric)", defaultHeader: "Grade #" },
  { field: "gradingCompany", group: "Grading", label: "Grading company", defaultHeader: "Grader" },
  { field: "gradingID", group: "Grading", label: "Cert / Grading ID", defaultHeader: "Cert Number" },
  { field: "qualifiers", group: "Grading", label: "Qualifiers (joined)", defaultHeader: "Qualifiers" },
  { field: "centering", group: "Grading", label: "Centering", defaultHeader: "Centering" },
  { field: "corners", group: "Grading", label: "Corners", defaultHeader: "Corners" },
  { field: "edges", group: "Grading", label: "Edges", defaultHeader: "Edges" },
  { field: "surface", group: "Grading", label: "Surface", defaultHeader: "Surface" },

  // Gemrate
  { field: "gemrateId", group: "Gemrate", label: "Gemrate ID", defaultHeader: "Gemrate ID" },
  { field: "gemrateUniversalId", group: "Gemrate", label: "Gemrate universal ID", defaultHeader: "Gemrate UID" },
  { field: "gemrateGrade", group: "Gemrate", label: "Gemrate grade", defaultHeader: "Gemrate Grade" },
  { field: "gemrateCardName", group: "Gemrate", label: "Gemrate card name", defaultHeader: "Gemrate Card Name" },
  { field: "gemrateDescription", group: "Gemrate", label: "Gemrate description", defaultHeader: "Gemrate Description" },
  { field: "specId", group: "Gemrate", label: "Spec ID", defaultHeader: "Spec ID" },
  { field: "setUrl", group: "Gemrate", label: "Set URL", defaultHeader: "Set URL" },

  // Pricing
  { field: "insuredValue", group: "Pricing", label: "Insured value", defaultHeader: "Insured" },
  { field: "oraclePrice", group: "Pricing", label: "Oracle price", defaultHeader: "Oracle Price" },
  { field: "suggestPrice", group: "Pricing", label: "Suggested price", defaultHeader: "Suggested Price" },

  // Storage
  { field: "vault", group: "Storage", label: "Vault", defaultHeader: "Vault" },
  { field: "vaultId", group: "Storage", label: "Vault ID", defaultHeader: "Vault ID" },
  { field: "location", group: "Storage", label: "Location (joined)", defaultHeader: "Location" },

  // Status
  { field: "status", group: "Status", label: "Status", defaultHeader: "Status" },
  { field: "nftStatus", group: "Status", label: "NFT status", defaultHeader: "NFT Status" },
  { field: "ReasonForRejection", group: "Status", label: "Rejection reason", defaultHeader: "Rejection Reason" },
  { field: "inSwap", group: "Status", label: "In swap", defaultHeader: "In Swap" },
  { field: "getBuybackOffer", group: "Status", label: "Get buyback offer", defaultHeader: "Get Buyback Offer" },

  // Timestamps
  { field: "createdAt", group: "Timestamps", label: "Created at", defaultHeader: "Created At" },
  { field: "updatedAt", group: "Timestamps", label: "Updated at", defaultHeader: "Updated At" },
  { field: "lastTransferredAt", group: "Timestamps", label: "Last transferred at", defaultHeader: "Last Transferred" },

  // Source images
  { field: "frontImage", group: "Images (source)", label: "Front image URL", defaultHeader: "Front Image" },
  { field: "backImage", group: "Images (source)", label: "Back image URL", defaultHeader: "Back Image" },
  { field: "animationUrl", group: "Images (source)", label: "Animation URL", defaultHeader: "Animation URL" },

  // CC CDN images (nested)
  { field: "images.front", group: "Images (CC CDN)", label: "Front (full)", defaultHeader: "Front (CDN)" },
  { field: "images.back", group: "Images (CC CDN)", label: "Back (full)", defaultHeader: "Back (CDN)" },
  { field: "images.frontM", group: "Images (CC CDN)", label: "Front (medium)", defaultHeader: "Front (M)" },
  { field: "images.backM", group: "Images (CC CDN)", label: "Back (medium)", defaultHeader: "Back (M)" },
  { field: "images.frontS", group: "Images (CC CDN)", label: "Front (small)", defaultHeader: "Front (S)" },
  { field: "images.backS", group: "Images (CC CDN)", label: "Back (small)", defaultHeader: "Back (S)" },

  // Owner (nested)
  { field: "owner.wallet", group: "Owner", label: "Owner wallet", defaultHeader: "Owner Wallet" },
  { field: "owner.evmWallet", group: "Owner", label: "Owner EVM wallet", defaultHeader: "Owner EVM" },
  { field: "owner.id", group: "Owner", label: "Owner ID", defaultHeader: "Owner ID" },
  { field: "owner.privyDid", group: "Owner", label: "Owner Privy DID", defaultHeader: "Owner Privy DID" },
  { field: "owner.role", group: "Owner", label: "Owner role", defaultHeader: "Owner Role" },
  { field: "owner.name", group: "Owner", label: "Owner name", defaultHeader: "Owner Name" },

  // Listing (nested, may be null)
  { field: "listing.price", group: "Listing", label: "Listing price", defaultHeader: "Listing Price" },
  { field: "listing.currency", group: "Listing", label: "Listing currency", defaultHeader: "Listing Currency" },
  { field: "listing.marketplace", group: "Listing", label: "Listing marketplace", defaultHeader: "Marketplace" },
  { field: "listing.id", group: "Listing", label: "Listing ID", defaultHeader: "Listing ID" },
  { field: "listing.sellerId", group: "Listing", label: "Listing seller ID", defaultHeader: "Seller ID" },
  { field: "listing.createdAt", group: "Listing", label: "Listed at", defaultHeader: "Listed At" },
  { field: "listing.updatedAt", group: "Listing", label: "Listing updated at", defaultHeader: "Listing Updated At" },
];

export const VALID_FIELDS: ReadonlySet<string> = new Set(SOURCE_FIELDS.map((f) => f.field));

export const FIELD_DEFS: ReadonlyMap<string, SourceFieldDef> = new Map(
  SOURCE_FIELDS.map((f) => [f.field, f]),
);

export function defaultHeaderFor(field: string): string {
  return FIELD_DEFS.get(field)?.defaultHeader ?? field;
}

// Group preserves insertion order from SOURCE_FIELDS.
export function groupedSourceFields(): Array<{ group: string; fields: SourceFieldDef[] }> {
  const out: Array<{ group: string; fields: SourceFieldDef[] }> = [];
  const idx = new Map<string, SourceFieldDef[]>();
  for (const f of SOURCE_FIELDS) {
    let bucket = idx.get(f.group);
    if (!bucket) {
      bucket = [];
      idx.set(f.group, bucket);
      out.push({ group: f.group, fields: bucket });
    }
    bucket.push(f);
  }
  return out;
}

function getPath(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function formatValue(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    return v
      .map((item) => (item == null ? "" : typeof item === "object" ? JSON.stringify(item) : String(item)))
      .filter((x) => x !== "")
      .join("; ");
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function flattenCard(card: CCCard): Record<string, string> {
  const row: Record<string, string> = {};
  for (const def of SOURCE_FIELDS) {
    if (def.field === "cardLink") {
      row[def.field] = card.nftAddress ? `${CARD_LINK_BASE}/${card.nftAddress}` : "";
    } else if (def.field === "comp" || def.field === STATIC_FIELD) {
      // comp is always empty by spec; static is sourced from the preset
      // column itself, not the card data.
      row[def.field] = "";
    } else {
      row[def.field] = formatValue(getPath(card, def.field));
    }
  }
  return row;
}

// Resolves a single CSV/table cell. Static columns pull from
// `column.value`; everything else looks up the flattened row.
export function resolveCellValue(
  row: Record<string, string>,
  column: { field: string; value?: string },
): string {
  if (column.field === STATIC_FIELD) return column.value ?? "";
  return row[column.field] ?? "";
}
